import { Injectable } from '@angular/core';
import { format, isDate } from 'date-fns';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import {
  ApplyTagGQL, ApplyTagInput, ApplyTagMutation, GetCalendarEventResponseGQL, GetEntityById2GQL,
  GetEntityConnectionGQL, TagType
} from 'generated/graphql';

import { reportHandledError } from 'core/report-handled-error';
import { describeFile, isNilOrEmpty } from 'helpers';

import { ApplyTagFormModel } from './ApplyTagFormModel';
import { TagFileAttachmentService } from './tag-file-attachment.service';

export interface ApplyTagsServiceInput {
  targetInfo: ApplyTagsTargetInfo;
  tags: ApplyTagFormModel[];
}

export interface ApplyTagsTargetInfo {
  targetType: 'Entity' | 'EntityConnection' | 'CalendarEventResponse';
  targetId: string;
  campaignId: string;
}

/** Which half of applying a tag failed. */
export type ApplyTagStage = 'applyTagMutation' | 'fileUpload';

export interface OnTagAppliedResponse {
  error?: any;
  mutationInput: ApplyTagInput;
  taggableLogbook?: { id: string };
  /** Set when `error` is set, so callers can tell the two failures apart. */
  stage?: ApplyTagStage;
}
/**
 * Apply Tags Service
 */
@Injectable({
  providedIn: 'root'
})
export class ApplyTagsService {

  constructor(
    private _applyTagGQL: ApplyTagGQL,
    private _getEntityByIdGQL: GetEntityById2GQL,
    private _getEntityConnection: GetEntityConnectionGQL,
    private _getCalendarEventResponseGQL: GetCalendarEventResponseGQL,
    private _tagFileAttachmentSvc: TagFileAttachmentService
  ) {
  }

  applyTags(
    data: ApplyTagsServiceInput,
    onApplied: (response: OnTagAppliedResponse) => void = (_) => {}) {

    const targetInfo = data.targetInfo;
    const inputs = this._createMutationInputsAndFiles(data);

    const mutations = inputs.map(val => {
      const input = val.mutationInput;
      const file = val.file;
      return this._applyTagGQL.mutate({ input }).pipe(
        switchMap((res) => {
          const taggable = (res['data'] as ApplyTagMutation).applyTag.taggableLogbook;

          if (!file) {
            return of({ mutationInput: input, error: null, taggableLogbook: taggable });
          }

          return this._tagFileAttachmentSvc
            .attachFileToTaggableLogbook(file, taggable.id).pipe(
              map(r => ({ mutationInput: input, error: null, taggableLogbook: r })),
              catchError(
                (err) => of(this._reportFailure(err, 'fileUpload', targetInfo, input, file))
              )
            );
        }),
        catchError(
          (err) => of(this._reportFailure(err, 'applyTagMutation', targetInfo, input, file))
        ),
        tap((res: OnTagAppliedResponse) => {
          onApplied(res);
        })
      );
    });

    return forkJoin(mutations).pipe(
      switchMap((responses) => {
        const errors = responses.filter(r => r.error);
        // Refetch the entity or entity connection so that taggables are all updated.
        if (targetInfo.targetType === 'EntityConnection') {
          return this._getEntityConnection.fetch(
            { entityConnectionId: targetInfo.targetId, campaignId: targetInfo.campaignId },
            {
              fetchPolicy: 'network-only'
            }).pipe(
              map(
                (res) => ({
                  target: res.data?.getEntityConnection,
                  errors
                })
              )
            );
        }

        if (targetInfo.targetType === 'CalendarEventResponse') {
          return this._getCalendarEventResponseGQL.fetch({
            id: targetInfo.targetId,
            campaignId: targetInfo.campaignId
          }, {
          fetchPolicy: 'network-only'
        }).pipe(
          map(
            (res) => ({
              target: res.data?.getCalendarEventResponse,
              errors
            })
          )
        );
        }

        return this._getEntityByIdGQL.fetch({
          entityId: targetInfo.targetId,
          campaignId: targetInfo.campaignId,
          withTags: true,
          withToplines: true
        }, {
          fetchPolicy: 'network-only'
        }).pipe(
          map(
            (res) => ({
              target: res.data?.getEntity,
              errors
            })
          )
        );
      })
    );

  }

  /**
   * Send a swallowed apply tag / upload failure to Sentry with enough context
   * to diagnose it, and shape it into the response the callers expect.
   *
   * These errors are caught so that one failed tag does not abort the rest of
   * the batch, which also means they never reach the global error handler.
   */
  private _reportFailure(
    error: any,
    stage: ApplyTagStage,
    targetInfo: ApplyTagsTargetInfo,
    input: ApplyTagInput,
    file: any
  ): OnTagAppliedResponse {
    reportHandledError(error, {
      operation: `applyTag.${stage}`,
      tags: {
        target_type: targetInfo.targetType,
        has_attachment: String(!!file)
      },
      extra: {
        stage,
        target_id: targetInfo.targetId,
        campaign_id: targetInfo.campaignId,
        tag_id: input.tagId,
        // The note is user entered, so log its shape rather than its content.
        note_length: input.note == null ? 0 : String(input.note).length,
        has_shift: !!input.shift,
        file: describeFile(file)
      }
    });

    return { error, mutationInput: input, stage };
  }

  private _createMutationInputsAndFiles(input: ApplyTagsServiceInput) {
    return input.tags
      .filter(v => !isNilOrEmpty(v?.selectedTag))
      .map(v => ({
        mutationInput: this.createMutationInput(input.targetInfo, v),
        file: v.file
      }));
  }

  private createMutationInput(targetInfo: ApplyTagsTargetInfo, applyTagModel: ApplyTagFormModel): ApplyTagInput {
    const v = applyTagModel;
    const formattedNote = this.formatNote(applyTagModel);
    return {
      targetId: targetInfo.targetId,
      targetType: targetInfo.targetType,
      tagId: v.selectedTag.id,
      campaignId: targetInfo.campaignId,
      note: formattedNote,
      shift: v.shiftInfo
    };
  }

  private formatNote(formValue: ApplyTagFormModel): string {
    if (isDate(formValue.note) && formValue.selectedTag.tagType === TagType.Date) {
      return format(formValue.note as any, 'yyyy-MM-dd');
    }

    return formValue.note;
  }
}
